import { Dispatch, ReactNode, SetStateAction, forwardRef, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { BaseTableStyled, BaseTableStyles, InputStyles, SelectStyles } from './style'
import { Table, GateTableColumn, Input, Select, TableProps, showNotification, Divider } from '@gate/gui'
import BaseLoadingOverlay from '../BaseLoadingOverlay'
import BaseEmpty from '../BaseEmpty'
import { useMemoizedFn } from 'ahooks'
import { Flex } from '@mantine/core'
import { replacePage, toScrollEle } from '@/utils/tools'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import BasePagination from '../BasePagination'
import SorterTitle from './SorterTableTitle'
import BaseCustomTable from '../BaseCustomTable'

export interface BaseListProps {
  page: number
  pageSize: number
  [key: string]: any
}

export interface BaseTableColumn<T> extends Omit<GateTableColumn, 'render' | 'title'> {
  width?: string | number
  render?: (item: T) => ReactNode
  /** 排序字段 key 接口key [+value,-value] */
  sort?: { [key: string]: [string, string] | undefined }
  title: ReactNode
  /** 表格额外的title */
  extraTitle?: ReactNode
  /** 合并列 */
  colSpan?: (item: T) => number
}

export interface BaseTableData<T> {
  page: number
  pageSize: number
  totalCount: number
  order?: string
  sort?: string
  list?: T[]
}

export interface BaseTableRef<T, P> {
  getParams: () => P
  setLoading: Dispatch<SetStateAction<boolean>>
  setData: Dispatch<SetStateAction<BaseTableData<T>>>
  fetchData: (params: P, ...args: any[]) => Promise<BaseTableData<T> | undefined>
  setSortMap: Dispatch<
    SetStateAction<
      Record<
        string,
        {
          type?: '+' | '-' | undefined
          key: string
        }
      >
    >
  >
}

export interface BaseTableProps<T, P> extends Omit<TableProps, 'dataSource' | 'columns'> {
  className?: string
  columns: BaseTableColumn<T>[]
  data?: BaseTableData<T>
  request?: (params: P, ...args: any[]) => Promise<BaseTableData<T>>
  /** 初始化参数 */
  initParams?: P
  id?: string
  toScrollTop?: number
  /** 切换分页是否添加到路由上 */
  isRouterPageToUrl?: boolean
  /** 排序点击回调函数 */
  onSortCb?: (key: string, sort?: '+' | '-') => void
  /** 是否显示分页选择器 */
  isSelectPageState?: boolean
  /** data改变函数  */
  onDataChangeCallback?: (data: T[]) => void
  /** 默认参数 */
  defaultArgs?: any[]
  /** 是否自定义table */
  isCustomTable?: boolean
  tfoot?: ReactNode
  tableKey?: string | null
  CustomCardTable?: React.FunctionComponent<any> | null
}

const BaseTable = <T extends Record<string, any>, P extends BaseListProps>() => {
  // eslint-disable-next-line react/display-name
  return forwardRef<BaseTableRef<T, P>, BaseTableProps<T, P>>((_props, ref) => {
    const { t } = useTranslation()
    const {
      id = 'base-table-scroll-id',
      toScrollTop = 92,
      isRouterPageToUrl = true,
      columns,
      data: initData,
      request,
      onSortCb,
      isSelectPageState = false,
      onDataChangeCallback,
      defaultArgs,
      isCustomTable,
      styles,
      className,
      tableKey: _tableKey,
      CustomCardTable,
      ...props
    } = _props

    const router = useRouter()

    const colWidths = useMemo(() => {
      return columns.map(col => {
        if (!col?.width) return 'auto'
        if (typeof col?.width === 'number') {
          return `${col?.width}px`
        } else {
          return col?.width
        }
      })
    }, [columns])

    const [loading, setLoading] = useState(false)
    const [data, setData] = useState<BaseTableData<T>>(initData as BaseTableData<T>)
    // 排序
    const [sortMap, setSortMap] = useState<Record<string, { type?: '+' | '-'; key: string }>>({})

    const [args, setArgs] = useState<any[]>(defaultArgs ?? [])
    const requestRef = useRef<any>(0)

    // 数据获取函数
    const fetchData = useMemoizedFn(async (params: P, ...rest) => {
      const requestId = Date.now()
      requestRef.current = requestId
      setArgs(rest)
      setLoading(true)

      if (!params?.sort as any) {
        setSortMap({})
      }

      try {
        delete params.pageCount
        const res = await request?.(params, ...rest)
        if (requestId === requestRef.current) {
          onDataChangeCallback?.(res?.list ?? [])
          setData({ ...params, ...res } as BaseTableData<T>)
          return res
        }
        return res
      } catch (error) {
        // showNotification({
        //   type: 'error',
        //   message: '',
        // })
      } finally {
        setLoading(false)
      }
    })

    // 页码改变函数
    const onChangePage = useMemoizedFn((page: number, params?: Record<string, any>) => {
      toScrollEle(id, toScrollTop)
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { list, totalCount, ...newParams } = { ...data, ...params, page }
      fetchData(newParams as any, ...args)

      if (isRouterPageToUrl) {
        const urlParams: any = { ...(router.query || {}) }
        delete urlParams.path

        if (Number(page) !== 1) {
          urlParams.page = page
        } else {
          delete urlParams.page
        }

        const queryString = new URLSearchParams(urlParams as Record<string, string>).toString()
        const newUrl = `${window.location.pathname}${queryString ? `?${queryString}` : ''}`

        window.history.pushState(null, '', newUrl)
      }
    })

    const [currentPage, setCurrentPage] = useState<string | undefined>('')

    // 分页器输入跳转
    const onKeyPress = useMemoizedFn(e => {
      if (Number(currentPage) === 0) {
        return
      }
      if (e.key === 'Enter') {
        setData(data => ({
          ...data,
          page: Number(currentPage),
        }))
        onChangePage(Number(currentPage))
      }
    })

    // 分页器数据
    const RowsPerPageSelectData = useMemo(() => {
      return [
        { value: '10', label: t('P.AmountEntriesPage', { Amount: 10 }) },
        { value: '15', label: t('P.AmountEntriesPage', { Amount: 15 }) },
        { value: '30', label: t('P.AmountEntriesPage', { Amount: 30 }) },
        { value: '50', label: t('P.AmountEntriesPage', { Amount: 50 }) },
      ]
    }, [t])

    const sortCallback = useMemoizedFn((index, sort: BaseTableColumn<T>['sort']) => {
      const key = Object.keys(sort ?? {})?.[0]
      const type = sortMap?.[index]?.type === '+' ? '-' : sortMap?.[index]?.type === '-' ? undefined : '+'
      setSortMap(params => {
        return {
          ...params,
          ...Object.fromEntries(
            Object.entries(params)?.map(([i, map]) => [
              i,
              {
                ...map,
                type: key !== map?.key ? undefined : undefined,
              },
            ])
          ),
          [index]: {
            type,
            key,
          },
        }
      })

      if (typeof onSortCb === 'function') {
        onSortCb?.(key, type)
        return
      } else {
        const { list, totalCount, ...newParams }: any = data

        if (!type) {
          const { sort, order, ...noParams }: any = newParams
          setData(({ sort, order, ...newData }: any) => newData)
          fetchData(noParams as any, ...args)
        } else {
          fetchData(
            {
              ...newParams,
              sort: key,
              order: type === '+' ? sort?.[key]?.[0] : sort?.[key]?.[1],
            } as any,
            ...args
          )
        }
      }
    })

    // new columns
    const newColumns = useMemo(() => {
      return columns.map((item, index) => {
        if (item?.sort) {
          item.renderTitle = title => (
            <SorterTitle type={sortMap?.[index]?.type} sortCallback={() => sortCallback(index, item?.sort)}>
              {/* {item?.extraTitle ? item?.extraTitle : null} */}
              {title}
            </SorterTitle>
          )
        }
        return item
      })
    }, [columns, sortCallback, sortMap])

    useImperativeHandle(ref, () => ({
      setData,
      fetchData,
      setSortMap,
      setLoading,
      getParams: () => {
        const { pageCount, totalCount, list, ...params } = data as any
        return params
      },
    }))

    const tableKey = `table-${data?.list?.map(item => item?.id)?.toString()}`

    return (
      <BaseTableStyled id={id} colWidths={colWidths} className={`${className}`}>
        {CustomCardTable ? <CustomCardTable data={data} /> : null}

        {!CustomCardTable && isCustomTable ? (
          <BaseCustomTable<T, P>
            key={_tableKey ?? tableKey}
            styles={styles ?? BaseTableStyles}
            loading={loading}
            loadingSection={<BaseLoadingOverlay style={{ alignItems: 'flex-start' }} visible={true} />}
            emptySection={<BaseEmpty />}
            {...props}
            columns={newColumns as any}
            data={data}
          />
        ) : null}

        {!CustomCardTable && !isCustomTable ? (
          <Table
            {...props}
            key={_tableKey ?? tableKey}
            styles={styles ?? BaseTableStyles}
            columns={newColumns as any}
            dataSource={data?.list ?? []}
            loading={loading}
            loadingSection={<BaseLoadingOverlay style={{ alignItems: 'flex-start' }} visible={true} />}
            emptySection={<BaseEmpty />}
          />
        ) : null}

        {data?.totalCount === 0 || (data?.page === 1 && !data?.list?.length) ? null : (
          <Flex gap={8} direction={{ base: 'column', sm: 'row' }} justify="center" align="center" mt={10}>
            <BasePagination
              // size={'sm'}
              page={Number(data?.page ?? router.query?.page)}
              total={data?.totalCount ? Math.ceil(data?.totalCount / (data?.pageSize || 20)) : 0}
              onChange={onChangePage}
            />
            {isSelectPageState && (
              <Flex gap={4} wrap="wrap" justify="center">
                <Select
                  data-locale={router.locale}
                  size="sm"
                  styles={SelectStyles}
                  type="primary"
                  data={RowsPerPageSelectData}
                  value={String(data?.pageSize)}
                  onChange={value => {
                    setData(data => ({
                      ...data,
                      page: 1,
                      pageSize: Number(value),
                    }))
                    onChangePage(1, { pageSize: Number(value) })
                  }}
                />
                <Flex align="center">
                  <span className="size-12 font-400 text-3">{t('P.JumperTo')}</span>
                  <Input
                    size="sm"
                    w={56}
                    styles={InputStyles}
                    value={currentPage}
                    onChange={(e: any) => {
                      const value = e.target.value
                      const total = data?.totalCount ? Math.ceil(data?.totalCount / (data?.pageSize || 20)) : 0
                      setCurrentPage(replacePage(value, total))
                    }}
                    onKeyPress={onKeyPress}
                  />
                  <span className="size-12 font-400 text-3">{t('P.Page')}</span>
                </Flex>
              </Flex>
            )}
          </Flex>
        )}
      </BaseTableStyled>
    )
  })
}

export default BaseTable
